// ============================================================
// useApplyHits - Post-hit effects orchestration hook.
// Extracted from useCombat applyHits (lines 184-662).
//
// Processes critical dice, condition immunity, weapon effects
// (via registry), horde magnitude, and applies state updates.
//
// The stages live in `./applyHits/`, in the order they run:
//   ledger   - the working state: chip limit, target, shared lists
//   crits    - critical dice and effects, the death floor, death
//   effects  - weapon specials, afflictions, exposures, fields
//   entry    - the attack-result entry and the one tab write
// ============================================================
import type { MutableRefObject } from 'react';
import { DEFAULT_STACK_POLICY, type StackPolicy } from '../engine/stackPolicy';
import type { Tab, NarrativeEntry } from '../types';
import { openLedger, type HitLedger, type LedgerRules } from './applyHits/ledger';
import { applyCriticals, resolveDeath } from './applyHits/crits';
import { applyWeaponEffects, forceFieldOverload, powerFieldParry } from './applyHits/effects';
import { commitAttack, hordeTally, woundsTally, type CommitSink } from './applyHits/entry';
import type { ApplyHitsData } from './applyHits/types';
import { cohesionCostOf } from '../engine/killTeam/cohesionDamage';

export type { ApplyHitsData } from './applyHits/types';

export interface UseApplyHitsParams {
  tabs: Tab[];
  syncNarrativeToFirestore: (entry: NarrativeEntry) => void;
  updateTab: (tabId: string, updater: (tab: Tab) => Tab) => void;
  addNarrative: (tabId: string, entry: NarrativeEntry) => void;
  historyTrack: MutableRefObject<boolean>;
  /**
   * GM house rule: timed conditions count down each round. Accepted for
   * call-site compatibility; the stacking a second Stunned gets is read
   * from `stackPolicy` since v8.367.0, so this hook no longer consults it.
   */
  roundCountdown?: boolean;
  /** What a condition does to one already worn (v8.367.0). Absent means extend. */
  stackPolicy?: StackPolicy;
  /** The table's protagonist ruling. Absent means off. */
  protagonistRules?: boolean;
  /**
   * Squad Mode (2026-10-23): this victim took heavy fire - 10+ raw damage from an Accurate gun, a Blast or a
   * Devastating weapon (`cohesionCostOf`). Whether he is a brother in Squad Mode, and what the leader's test says, is
   * the receiver's to decide. Called once per victim, after the hits are applied.
   */
  onCohesionHit?: (tabId: string, weapon: string) => void;
}

export interface UseApplyHitsReturn {
  applyHits: (tabId: string, data: ApplyHitsData) => void;
}

/** An error anywhere in the application lands in the log, never lost. */
function applyErrorEntry(err: unknown): NarrativeEntry {
  return {
    type: 'info',
    text: '\u26A0 Apply error: ' + (err instanceof Error ? err.message : String(err)),
    timestamp: Date.now(),
  };
}

/**
 * A RAM's bow (plan map-display, D; engine/actions/ram): when the ram's hit
 * lands - not when the target Dodged it - the rammer takes the GM's share, as
 * ruled, unsoaked. Returns the rammer's tab write, to make once the attack is
 * committed, and says so in the target's log; null for any other attack.
 */
function bowShare(ledger: HitLedger, tabs: readonly Tab[]): { id: string; write: (t: Tab) => Tab } | null {
  const { data } = ledger;
  const bow = data.slot.ramBow ?? 0;
  const rammer = data.actorId ? tabs.find((t) => t.id === data.actorId) : undefined;
  if (bow <= 0 || !rammer || data.hitsAfterDefence.length === 0) return null;
  const text = `🛞 The bow takes ${bow}: ${rammer.target.name} rammed them.`;
  ledger.extraNarrative.push({ type: 'info', text, timestamp: Date.now() });
  const write = (t: Tab) => ({
    ...t,
    target: { ...t.target, wounds: { ...t.target.wounds, current: t.target.wounds.current - bow } },
  });
  return { id: rammer.id, write };
}

/** One attack's hits onto one tab, stage by stage - see the file header. */
function applyToTab(
  tabId: string,
  tab: Tab,
  data: ApplyHitsData,
  deps: LedgerRules & CommitSink & { tabs: readonly Tab[] },
): void {
  const ledger = openLedger(tabId, tab, data, deps);
  applyCriticals(ledger);
  resolveDeath(ledger);
  applyWeaponEffects(ledger);
  const forceFieldUpdate = forceFieldOverload(ledger);
  powerFieldParry(ledger);
  const tally = tab.target.horde?.isHorde ? hordeTally(ledger) : woundsTally(ledger);
  const bow = bowShare(ledger, deps.tabs);
  commitAttack(ledger, tally, forceFieldUpdate, deps);
  if (bow) deps.updateTab(bow.id, bow.write);
}

export default function useApplyHits({
  tabs,
  syncNarrativeToFirestore,
  updateTab,
  addNarrative,
  historyTrack,
  stackPolicy = DEFAULT_STACK_POLICY,
  protagonistRules = false,
  onCohesionHit,
}: UseApplyHitsParams): UseApplyHitsReturn {
  const applyHits = (tabId: string, data: ApplyHitsData) => {
    try {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;
      applyToTab(tabId, tab, data, {
        stacking: stackPolicy,
        protagonistRules,
        syncNarrativeToFirestore,
        updateTab,
        tabs,
      });
      historyTrack.current = true;
      if (onCohesionHit && cohesionCostOf(data)) onCohesionHit(tabId, data.slot.weaponName || data.slot.name);
    } catch (err) {
      addNarrative(tabId, applyErrorEntry(err));
    }
  };

  return { applyHits };
}
